import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Palette, Image, Type, Globe, Save, RotateCcw, Eye, Check, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';
import { toast } from 'sonner';

const ColorInput = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs text-[#8DA0B9] mb-1 block">{label}</label>
    <div className="flex items-center gap-2">
      <div className="w-9 h-9 rounded-lg border border-[#DCE4ED] overflow-hidden flex-shrink-0 cursor-pointer relative">
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full cursor-pointer opacity-0" data-testid={testId} />
        <div className="w-full h-full" style={{ backgroundColor: value }} />
      </div>
      <Input value={value} onChange={e => onChange(e.target.value)} className="font-mono text-xs h-9" />
    </div>
  </div>
);

const BrandingAdmin = () => {
  const [branding, setBranding] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getMyBranding();
        setBranding(data);
        setOriginal(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (k, v) => setBranding(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await api.updateBranding(branding);
      setBranding(result);
      setOriginal(result);
      toast.success('Branding updated successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setBranding(original);
    toast.info('Changes reverted');
  };

  const hasChanges = JSON.stringify(branding) !== JSON.stringify(original);

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="branding-admin-page">
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={18} /><span className="text-sm">Admin</span>
            </Link>
            <div className="w-px h-6 bg-[#DCE4ED]" />
            <h1 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>White-Label Branding</h1>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Button variant="outline" size="sm" onClick={handleReset} data-testid="branding-reset-btn">
                <RotateCcw size={14} className="mr-1" /> Revert
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={!hasChanges || saving} className="bg-[#1A3A64] hover:bg-[#0D1B2A] text-white" data-testid="branding-save-btn">
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Editor */}
          <div className="lg:col-span-3 space-y-6">
            {/* Identity */}
            <div className="bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="branding-identity">
              <div className="flex items-center gap-3 mb-5">
                <Type size={18} className="text-[#1A3A64]" />
                <h2 className="text-base font-semibold text-[#0D1B2A]">Identity</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#8DA0B9] mb-1 block">Company Name</label>
                  <Input value={branding?.company_name || ''} onChange={e => update('company_name', e.target.value)} data-testid="branding-company-name" />
                </div>
                <div>
                  <label className="text-xs text-[#8DA0B9] mb-1 block">Tagline</label>
                  <Input value={branding?.tagline || ''} onChange={e => update('tagline', e.target.value)} data-testid="branding-tagline" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-[#8DA0B9] mb-1 block">Logo URL</label>
                  <Input value={branding?.logo_url || ''} onChange={e => update('logo_url', e.target.value)} placeholder="https://..." data-testid="branding-logo-url" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-[#8DA0B9] mb-1 block">Custom Domain</label>
                  <Input value={branding?.custom_domain || ''} onChange={e => update('custom_domain', e.target.value)} placeholder="insurance.yourdomain.com" data-testid="branding-domain" />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="branding-colors">
              <div className="flex items-center gap-3 mb-5">
                <Palette size={18} className="text-[#1A3A64]" />
                <h2 className="text-base font-semibold text-[#0D1B2A]">Colors</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <ColorInput label="Primary" value={branding?.primary_color || '#1A3A64'} onChange={v => update('primary_color', v)} testId="branding-primary" />
                <ColorInput label="Secondary" value={branding?.secondary_color || '#00AEEF'} onChange={v => update('secondary_color', v)} testId="branding-secondary" />
                <ColorInput label="Accent" value={branding?.accent_color || '#00C4AD'} onChange={v => update('accent_color', v)} testId="branding-accent" />
                <ColorInput label="Background" value={branding?.bg_color || '#F0F4F8'} onChange={v => update('bg_color', v)} testId="branding-bg" />
                <ColorInput label="Text" value={branding?.text_color || '#0D1B2A'} onChange={v => update('text_color', v)} testId="branding-text" />
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-4">
              <div className="flex items-center gap-2 text-sm text-[#8DA0B9]">
                <Eye size={14} /> <span>Live Preview</span>
              </div>
              <motion.div
                key={JSON.stringify(branding)}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                className="rounded-xl border border-[#DCE4ED] overflow-hidden shadow-lg"
                data-testid="branding-preview"
              >
                {/* Preview Header */}
                <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: branding?.primary_color || '#1A3A64' }}>
                  <div className="flex items-center gap-2">
                    {branding?.logo_url ? (
                      <img src={branding.logo_url} alt="" className="h-6 w-6 rounded object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                        <Shield size={14} className="text-white" />
                      </div>
                    )}
                    <span className="text-sm font-semibold text-white">{branding?.company_name || 'InsureLab'}</span>
                  </div>
                  <div className="flex gap-2">
                    {['Home', 'Quote', 'Plans'].map(t => (
                      <span key={t} className="text-[10px] text-white/70">{t}</span>
                    ))}
                  </div>
                </div>

                {/* Preview Hero */}
                <div className="px-4 py-8 text-center" style={{ backgroundColor: branding?.bg_color || '#F0F4F8' }}>
                  <p className="text-lg font-bold mb-1" style={{ color: branding?.text_color || '#0D1B2A' }}>
                    {branding?.tagline || 'AI-Powered Insurance Builder'}
                  </p>
                  <p className="text-xs mb-4" style={{ color: `${branding?.text_color || '#0D1B2A'}80` }}>
                    Build your perfect policy in minutes
                  </p>
                  <div className="flex justify-center gap-2">
                    <div className="px-4 py-1.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: branding?.secondary_color || '#00AEEF' }}>
                      Get Started
                    </div>
                    <div className="px-4 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: branding?.accent_color || '#00C4AD', color: branding?.accent_color || '#00C4AD' }}>
                      Learn More
                    </div>
                  </div>
                </div>

                {/* Preview Cards */}
                <div className="px-4 py-4 grid grid-cols-3 gap-2" style={{ backgroundColor: branding?.bg_color || '#F0F4F8' }}>
                  {['Liability', 'Cyber', 'Property'].map(c => (
                    <div key={c} className="bg-white rounded-lg p-2 text-center shadow-sm">
                      <div className="w-6 h-6 mx-auto rounded-lg mb-1 flex items-center justify-center" style={{ backgroundColor: `${branding?.secondary_color || '#00AEEF'}15` }}>
                        <Shield size={12} style={{ color: branding?.secondary_color || '#00AEEF' }} />
                      </div>
                      <p className="text-[10px] font-medium" style={{ color: branding?.text_color || '#0D1B2A' }}>{c}</p>
                      <p className="text-[8px]" style={{ color: `${branding?.text_color || '#0D1B2A'}60` }}>$150/mo</p>
                    </div>
                  ))}
                </div>

                {/* Preview Footer */}
                <div className="px-4 py-2 text-center" style={{ backgroundColor: branding?.primary_color || '#1A3A64' }}>
                  <p className="text-[9px] text-white/50">{branding?.company_name || 'InsureLab'} &bull; Powered by InsureLab Platform</p>
                </div>
              </motion.div>

              {branding?.custom_domain && (
                <div className="flex items-center gap-2 text-xs p-3 bg-white rounded-lg border border-[#DCE4ED]">
                  <Globe size={14} className="text-[#00AEEF]" />
                  <span className="text-[#8DA0B9]">Domain:</span>
                  <span className="text-[#0D1B2A] font-medium">{branding.custom_domain}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingAdmin;
